import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { adminApi } from "../api";
import { Btn, PageHead, StatusPill, fmtDate } from "../components";

const TABS = [
  { key: "", label: "ALL" },
  { key: "featured", label: "GET FEATURED" },
  { key: "partner", label: "PARTNERS" },
  { key: "writer", label: "CONTRIBUTING WRITERS" },
  { key: "contact", label: "CONTACT" },
];
const STATUSES = ["New", "Reviewing", "Contacted", "Closed"];
const TYPE_LABELS = { featured: "GET FEATURED", partner: "PARTNER", writer: "WRITER", contact: "CONTACT" };
const FIELD_LABELS = { phone: "PHONE", company: "COMPANY / BRAND / PROJECT", website: "WEBSITE / PORTFOLIO", social: "SOCIAL", subject: "FEATURED FOR / TYPE", interests: "AREAS OF INTEREST", experience: "WRITING EXPERIENCE / SAMPLE", message: "MESSAGE / STORY", extra: "ADDITIONAL INFO", start_date: "PREFERRED START", end_date: "PREFERRED END", date_preference: "DATE FLEXIBILITY", timing_notes: "TIMING NOTES", budget_label: "BUDGET RANGE", opportunity: "OPPORTUNITY INTEREST" };
const WIDE_FIELDS = ["message", "experience", "extra", "timing_notes", "opportunity"];

export default function Inquiries() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("");
  const [open, setOpen] = useState(null);

  const load = () => adminApi.get(`/admin/inquiries${tab ? `?type=${tab}` : ""}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [tab]);

  const setStatus = async (inq, status) => {
    await adminApi.patch(`/admin/inquiries/${inq.id}`, { status });
    load();
  };

  const remove = async (inq) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await adminApi.delete(`/admin/inquiries/${inq.id}`);
    load();
  };

  return (
    <div data-testid="admin-inquiries">
      <PageHead title="INQUIRIES" sub="Feature requests, brand inquiries, contributing writers and general contact." />

      <div className="flex flex-wrap gap-2 mb-6" data-testid="inquiries-tabs">
        {TABS.map((t) => (
          <button
            key={t.key} onClick={() => setTab(t.key)}
            data-testid={`inquiries-tab-${t.label.toLowerCase().replace(/\s/g, "-")}`}
            className={`font-mono text-[10px] tracking-[0.18em] px-4 py-2 rounded-full border transition-colors ${tab === t.key ? "bg-burnt border-burnt text-cream" : "border-woodborder text-clay hover:text-tan"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="border border-woodborder" data-testid="inquiries-list">
        {items.length === 0 && <p className="p-8 font-mono text-xs text-clay text-center" data-testid="inquiries-empty">NO INQUIRIES HERE YET.</p>}
        {items.map((inq, i) => (
          <div key={inq.id} className="border-b border-woodborder last:border-b-0" data-testid={`inquiry-row-${i}`}>
            <div className="flex items-center gap-3 px-4 md:px-6 py-4 hover:bg-card/30 transition-colors flex-wrap">
              <span className="font-mono text-[9px] tracking-[0.15em] bg-tan/10 text-tan border border-tan/30 px-2.5 py-1 rounded-full shrink-0">{TYPE_LABELS[inq.type] || inq.type}</span>
              <div className="flex-1 min-w-[160px]">
                <p className="font-editorial font-bold text-cream text-sm">{inq.name}</p>
                <p className="font-mono text-[10px] text-clay mt-0.5">{inq.email} — {fmtDate(inq.created_at)}</p>
              </div>
              <select
                value={inq.status} onChange={(e) => setStatus(inq, e.target.value)}
                data-testid={`inquiry-status-${i}`}
                className="bg-obsidian border border-woodborder text-sand font-mono text-[10px] tracking-[0.1em] px-3 py-2 rounded-sm outline-none"
              >
                {STATUSES.map((s) => <option key={s} value={s} className="bg-wood">{s}</option>)}
              </select>
              <StatusPill status={inq.status} />
              <button onClick={() => setOpen(open === inq.id ? null : inq.id)} className="p-2 text-clay hover:text-cream" data-testid={`inquiry-expand-${i}`} aria-label="Expand">
                {open === inq.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              <button onClick={() => remove(inq)} className="p-2 text-clay hover:text-red-400" data-testid={`inquiry-delete-${i}`} aria-label="Delete"><Trash2 size={15} /></button>
            </div>
            {open === inq.id && (
              <div className="px-4 md:px-6 pb-6 pt-1 grid sm:grid-cols-2 gap-x-8 gap-y-3 bg-obsidian/40" data-testid={`inquiry-detail-${i}`}>
                {Object.entries(FIELD_LABELS).map(([k, label]) =>
                  inq[k] ? (
                    <div key={k} className={WIDE_FIELDS.includes(k) ? "sm:col-span-2" : ""}>
                      <p className="font-mono text-[9px] tracking-[0.2em] text-tan">{label}</p>
                      <p className="mt-1 text-sand text-sm font-light whitespace-pre-wrap">{inq[k]}</p>
                    </div>
                  ) : null
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
